import React, {useEffect, useState, useRef, useCallback} from 'react';
import {useNotificationContext} from 'contexts/NotificationContext';
import {useKDMaxContext} from 'components/customer/KDMax/contexts/KDMaxContext';
import {
    genericMessageHandler,
    usePromise,
    xmlFileReader,
    xmlFileParser,
} from 'shared/helpers';
import {
    addImportedProducts,
    deleteAllImportedProduct,
    getAllKDMaxProducts,
} from 'service/KDMaxImportService';
import {getQuickProducts} from 'service/ProductService';
import XMLParserError from 'shared/errors/XMLParserError';
import {useNavigate, useParams} from 'react-router-dom';
import {KDMaxImportMessage} from 'components/customer/KDMax/KDMaxImportMessage';
import {contentParser} from 'shared/helpers/KDMaxHelpers';

export const useKDMaxImport = () => {
    const [loader, setLoader] = useState(false);
    const [disabled, setDisabled] = useState(true);
    const {cabinets, setCabinets, showDialog, dialog, hideDialog} =
        useKDMaxContext();

    const {notify} = useNotificationContext();
    const {jobId, roomId} = useParams();
    const navigate = useNavigate();
    const message = useRef({modalVisibility: false, message: ''});

    const handleCheck = useCallback(
        (event) => setDisabled(!event.target.checked),
        [setDisabled]
    );

    const displayMessages = () => {
        if (message.current.modalVisibility) {
            showDialog({
                title: 'KD Max Import Complete',
                message: (
                    <KDMaxImportMessage
                        dialogString={message.current.message}
                        handleCheck={handleCheck}
                    />
                ),
                hideYesButton: true,
                hideNoButton: true,
                buttons: [
                    {
                        name: 'Ok',
                        show: true,
                        disabled: disabled,
                        action: () => {
                            navigate(location.pathname);
                            hideDialog();
                        },
                    },
                ],
            });
        }
    };

    const fileUploadHandler = async (event) => {
        try {
            const {contents, error} = await xmlFileReader(event.target.files);

            if (error) {
                throw new XMLParserError(error);
            }

            await xmlFileParser(contents);

            for (const item of contents) {
                const {cabinetData, dialogString} = await contentParser(
                    item,
                    jobId,
                    roomId,
                    genericMessageHandler,
                    notify
                );
                if (cabinetData.length) {
                    await addImportedProducts(cabinetData);
                    message.current.message = dialogString;
                    message.current.modalVisibility = true;
                    setDisabled(true);
                    setLoader(false);
                    displayMessages();
                    setCabinets({
                        type: 'add',
                        payload: [...cabinets, ...cabinetData],
                    });
                }
            }
        } catch (e) {
            event.target.value = '';
            genericMessageHandler(notify, {
                message:
                    e instanceof XMLParserError
                        ? e.message
                        : 'Invalid xml file',
            });
            setLoader(false);
        }
    };

    const deleteAllProducts = async () => {
        setLoader(true);
        deleteAllImportedProduct();
        setCabinets({
            type: 'deleteAll',
            payload: [],
        });
        setLoader(false);
    };

    useEffect(() => {
        displayMessages();
    }, [disabled]);

    useEffect(() => {
        setLoader(true);
        return usePromise(
            ([cabinets]) => {
                cabinets.sort((a, b) => {
                    return a.isAdded === b.isAdded ? 0 : a ? -1 : 1;
                });
                setCabinets({
                    type: 'add',
                    payload: cabinets,
                });

                setLoader(false);
            },
            [getAllKDMaxProducts(roomId), getQuickProducts()],
            (error) => {
                genericMessageHandler(notify, error);
                setLoader(false);
            }
        );
    }, [roomId]);

    return {
        loader,
        cabinets,
        dialog,
        fileUploadHandler,
        deleteAllProducts,
    };
};
